/**
 * Page d'inscription
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import LocaliteSelector from '../components/forms/LocaliteSelector.jsx';
import CountryPhoneInput from '../components/forms/CountryPhoneInput.jsx';
import PasswordInput from '../components/forms/PasswordInput.jsx';
import EulaModal from '../components/EulaModal.jsx';
import VoiceNoteRecorder from '../components/forms/VoiceNoteRecorder.jsx';
import { blobToBase64 } from '../utils/audioRecording.js';
import { defaultCountry, getCountryByPhoneCode, getCountryByCode } from '../data/countries.js';
import { describePrimaryOtpChannel } from '../utils/otpChannel.js';
import {
  getPhoneCountryMismatchMessage,
  isPhoneAlignedWithCountry,
  isStrictPhoneCountryAlignment,
  requiresPhoneCountryMismatchAck,
} from '../utils/phone-country-alignment.js';
import { validateFullPhoneForSubmit, extractLocalDigits } from '../utils/phone-input.util.js';
import api from '../services/api.js';
import * as authService from '../services/auth.service.js';
import { useErrorToast } from '../hooks/useErrorToast.js';
import { useGeolocation } from '../hooks/useGeolocation.js';
import RoleIcon from '../components/RoleIcon.jsx';
import ElectionEligibilityField from '../components/elections/ElectionEligibilityField.jsx';
import { professionOptions } from '../constants/professions.js';
import './Register.css';

const USER_TYPES = [
  {
    value: 'parrain_grand',
    label: 'Parrain Grand Électeur',
    role: 'parrain_grand',
    description: 'Mobilisateur civique : influence votre entourage et organise des campagnes d\'enrôlement via vos Relais Privés. Finance complémentaire quand un dossier est bloqué. Résidence au Sénégal ou diaspora. Validation par le Gouverneur. Dashboard : campagnes, suivi des bénéficiaires, demandes de fonds et rapports.'
  },
  {
    value: 'parrain_sympa',
    label: 'Parrain Sympathisant',
    role: 'parrain_sympa',
    description: 'Contributeur qui participe au financement via un pool général ou un bouquet de bénéficiaires. Réside au Sénégal ou à l\'étranger. Compte activé immédiatement sans validation. Dashboard simplifié avec suivi des contributions et impact collectif.'
  },
  {
    value: 'relais',
    label: 'Relais Public',
    role: 'relais_public',
    description: 'Personne ou structure au Sénégal qui accompagne les bénéficiaires de sa zone sur le terrain : diagnostics, parcours personnalisés et campagnes d\'enrôlement. Compte validé par un Gouverneur. (Un Relais Privé rejoint la plateforme uniquement via le lien d\'invitation d\'un Parrain Grand Électeur.)'
  },
  {
    value: 'beneficiaire',
    label: 'Bénéficiaire',
    role: 'beneficiaire',
    description: 'Citoyen au Sénégal qui bénéficie de l\'accompagnement et du financement pour son inscription sur les listes électorales. Inscrivez-vous pour contacter le Relais Public le plus proche de chez vous.'
  },
];

function Register() {
  const [selectedAccountType, setSelectedAccountType] = useState(null); // Type de compte sélectionné
  const [formData, setFormData] = useState({
    telephone: defaultCountry.phoneCode, // Commence avec le code pays par défaut
    password: '',
    confirmPassword: '',
    type: '',
    email: '',
    // Nouveaux champs Version 3.0
    first_name: '',
    last_name: '',
    profession: '',
    country: defaultCountry.name, // Sénégal par défaut
    country_code: defaultCountry.code, // Code ISO du pays
    region_id: '',
    department_id: '',
    commune_id: '',
    quartier: '',
    // Champs pour adresses internationales (non-Sénégal)
    state: '',
    city: '',
    postal_code: '',
    address_line: '',
    // Éligibilité 18 ans — Bénéficiaire uniquement (porte C, v4.64.0)
    date_naissance: '',
    election_id: null,
    // Champs spécifiques Relais
    relais_type: 'public', // Type de Relais par défaut
    relais_invitation: null, // Données d'invitation si Relais Privé
    // Motivation pour relais public
    motivation_type: '',
    motivation_text: '',
    motivation_audio: null,
    // GPS Location
    latitude: null,
    longitude: null,
    precision_meters: null,
    geolocation_source: null,
  });
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({}); // Erreurs par champ
  useErrorToast(error);
  const [loading, setLoading] = useState(false);
  const [registrationDone, setRegistrationDone] = useState(null); // Écran de confirmation après inscription (compte en attente)
  const [capturingGeolocation, setCapturingGeolocation] = useState(false);
  const [gpsStatus, setGpsStatus] = useState(null); // 'captured', 'failed', 'not_available' - used internally, not shown to user
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeMessage, setCodeMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  // Canal OTP courant (sms / voice / email) — piloté pour la logique d'envoi ;
  // l'affichage s'appuie sur otpPreview / fallbackChannel.
  const [, setVerificationChannel] = useState('sms');
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const [fallbackAvailable, setFallbackAvailable] = useState(false);
  const [fallbackChannel, setFallbackChannel] = useState(null); // 'email' | 'voice'
  const [fallbackEmail, setFallbackEmail] = useState('');
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [mockOtpCode, setMockOtpCode] = useState('');
  const [otpRequired, setOtpRequired] = useState(true); // Défaut sûr : OTP exigé tant que la config n'est pas chargée
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [showEula, setShowEula] = useState(false);
  const [motivationVoice, setMotivationVoice] = useState(null); // { blob, mime, durationS } | null (motivation Relais Public)
  const [phoneCountryMismatchAck, setPhoneCountryMismatchAck] = useState(false);
  // Verdict d'éligibilité renvoyé par <ElectionEligibilityField> ; null = non confirmé.
  const [eligibility, setEligibility] = useState(null);
  const { register, logout } = useAuth();
  const { captureSilent } = useGeolocation();
  const navigate = useNavigate();

  // Relais & Bénéficiaire : Sénégal par défaut (majorité des comptes), diaspora autorisée.
  const isRelaisOrBeneficiaire =
    selectedAccountType === 'relais' || selectedAccountType === 'beneficiaire';

  const otpPreview = describePrimaryOtpChannel(formData.country_code);

  const phoneCountryMismatch = useMemo(
    () => selectedAccountType && !isPhoneAlignedWithCountry(formData.telephone, formData.country_code),
    [selectedAccountType, formData.telephone, formData.country_code],
  );

  const phoneReadyForOtp = useMemo(
    () => validateFullPhoneForSubmit(formData.telephone, formData.country_code).valid,
    [formData.telephone, formData.country_code],
  );

  // Charger la configuration publique d'inscription (OTP obligatoire ou non)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authService.getRegistrationConfig();
        if (!cancelled && res?.success && typeof res.data?.otpRequired === 'boolean') {
          setOtpRequired(res.data.otpRequired);
        }
      } catch {
        // En cas d'échec, on conserve le défaut sûr (OTP exigé)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccountTypeSelect = (type) => {
    setSelectedAccountType(type);
    const preferSenegalDefault = type === 'relais' || type === 'beneficiaire';
    setFormData({
      ...formData,
      type: type,
      ...(preferSenegalDefault && !formData.country_code && {
        country: defaultCountry.name,
        country_code: defaultCountry.code,
        telephone: formData.telephone?.startsWith('+') ? formData.telephone : defaultCountry.phoneCode,
      }),
    });
    setError('');
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'file' ? files[0] : value,
    });
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
    if (error) {
      setError('');
    }
  };

  const handleLocaliteChange = (updates) => {
    const invitationLocksPhone =
      selectedAccountType === 'relais' && !!formData.relais_invitation;
    // Si le pays change, mettre à jour le code téléphonique immédiatement
    if (updates.phone_code && !invitationLocksPhone) {
      const country = getCountryByPhoneCode(updates.phone_code, updates.country_code);
      const prevCountry = getCountryByCode(formData.country_code);
      if (country && country.phoneCode !== prevCountry.phoneCode) {
        setFormData((prev) => {
          const prevCountry = getCountryByCode(prev.country_code);
          const currentNumber = extractLocalDigits(prev.telephone, prevCountry);
          return {
            ...prev,
            ...updates,
            telephone: currentNumber ? `${country.phoneCode}${currentNumber}` : country.phoneCode,
          };
        });
        resetOtpSession();
        setPhoneCountryMismatchAck(false);
        setVerificationChannel(describePrimaryOtpChannel(updates.country_code).primary);
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates,
    }));
    if (updates.country_code) {
      resetOtpSession();
      setPhoneCountryMismatchAck(false);
      setVerificationChannel(describePrimaryOtpChannel(updates.country_code).primary);
    }
  };

  const handlePhoneChange = (fullPhoneNumber) => {
    setFormData({
      ...formData,
      telephone: fullPhoneNumber,
    });
    setPhoneCountryMismatchAck(false);
    setPhoneVerified(false);
    setPhoneVerificationToken('');
    setVerificationCode('');
    setCodeMessage('');
    setMockOtpCode('');
    setShowEmailFallback(false);
    setFallbackAvailable(false);
    setFallbackChannel(null);
    setVerificationChannel(describePrimaryOtpChannel(formData.country_code).primary);
  };

  const resetOtpSession = () => {
    setPhoneVerified(false);
    setPhoneVerificationToken('');
    setVerificationCode('');
    setCodeMessage('');
    setMockOtpCode('');
    setShowEmailFallback(false);
    setFallbackAvailable(false);
    setFallbackChannel(null);
  };

  const buildSendCodeMessage = (channel, resData) => {
    const isMock = Boolean(resData?.mock_code);
    const mockBlocked = resData?.mock_code_ui_blocked || (resData?.provider === 'mock' && !isMock);
    if (isMock) {
      if (channel === 'voice') {
        return 'Mode test : parcours « appel vocal » simulé — aucun appel réel. Utilisez le code ci-dessous.';
      }
      if (channel === 'email') {
        return 'Mode test : aucun email réel. Utilisez le code ci-dessous.';
      }
      return 'Mode test : aucun SMS réel envoyé. Utilisez le code ci-dessous.';
    }
    if (mockBlocked) {
      return (
        'Mode simulation actif : aucun envoi réel. Le code n\'est pas affiché — '
        + 'demandez à l\'administrateur d\'activer « Afficher le code OTP sur la page Inscription » '
        + 'dans Admin → OTP Configuration → Paramètres généraux.'
      );
    }
    if (channel === 'voice') {
      return 'Vous allez recevoir un appel vocal qui vous dictera votre code. Répondez à l\'appel.';
    }
    if (channel === 'email') {
      return 'Code envoyé par email. Vérifiez votre boîte de réception (et les spams).';
    }
    return 'Code envoyé par SMS. Vérifiez votre téléphone.';
  };

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSendPhoneCode = async (channelOverride) => {
    const phoneFormatCheck = validateFullPhoneForSubmit(formData.telephone, formData.country_code);
    if (!phoneFormatCheck.valid) {
      setError(phoneFormatCheck.message);
      return;
    }

    if (
      phoneCountryMismatch
      && selectedAccountType
      && isStrictPhoneCountryAlignment(selectedAccountType)
    ) {
      setError(getPhoneCountryMismatchMessage(formData.country_code));
      return;
    }

    setSendingCode(true);
    setCodeMessage('');
    setMockOtpCode('');
    setError('');
    setFallbackAvailable(false);
    setFallbackChannel(null);
    try {
      const res = await authService.sendPhoneCode(formData.telephone, {
        countryCode: formData.country_code,
        userType: selectedAccountType,
        ...(channelOverride ? { channel: channelOverride } : {}),
      });
      if (res.success) {
        const channel = res.data?.channel || 'sms';
        setVerificationChannel(channel);
        setCodeMessage(buildSendCodeMessage(channel, res.data));
        if (res.data?.mock_code) {
          setMockOtpCode(res.data.mock_code);
        }
        setResendCooldown(60);
        setPhoneVerified(false);
        setPhoneVerificationToken('');
        setVerificationCode('');
      } else {
        setError(res.error || 'Impossible d\'envoyer le code');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible d\'envoyer le code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSendEmailCode = async () => {
    const email = (fallbackEmail || formData.email || '').trim();
    if (!email) {
      setError('Veuillez saisir une adresse email pour recevoir le code.');
      return;
    }
    setSendingEmailCode(true);
    setCodeMessage('');
    setMockOtpCode('');
    setError('');
    try {
      const res = await authService.sendEmailCode(formData.telephone, email);
      if (res.success) {
        setVerificationChannel('email');
        setFallbackAvailable(false);
        setFallbackChannel(null);
        if (res.data?.mock_code) {
          setMockOtpCode(res.data.mock_code);
          setCodeMessage('Mode test : aucun email réel envoyé. Utilisez le code ci-dessous.');
        } else if (res.data?.mock_code_ui_blocked || res.data?.provider === 'mock') {
          setCodeMessage(
            'Mode simulation actif : aucun email réel. Le code n\'est pas affiché — '
            + 'activez « Afficher le code OTP sur la page Inscription » dans Admin → OTP Configuration.',
          );
        } else {
          setCodeMessage(`Code envoyé par email à ${email}. Vérifiez votre boîte de réception (et les spams).`);
        }
        setResendCooldown(60);
        setPhoneVerified(false);
        setPhoneVerificationToken('');
      } else {
        setError(res.error || 'Impossible d\'envoyer le code par email');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible d\'envoyer le code par email');
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    setVerifyingCode(true);
    setCodeMessage('');
    setError('');
    try {
      const res = await authService.verifyPhoneCode(formData.telephone, verificationCode, {
        countryCode: formData.country_code,
      });
      if (res.success && res.data?.phone_verification_token) {
        setPhoneVerified(true);
        setPhoneVerificationToken(res.data.phone_verification_token);
        setFallbackAvailable(false);
        setFallbackChannel(null);
        setCodeMessage('Numéro vérifié ✓');
      } else {
        setError(res.error || 'Code invalide');
      }
    } catch (err) {
      const data = err.response?.data || {};
      setError(data.error || 'Code invalide');
      if (data.fallbackAvailable && data.fallbackChannel) {
        setFallbackAvailable(true);
        setFallbackChannel(data.fallbackChannel);
        if (data.fallbackChannel === 'email') {
          setShowEmailFallback(true);
          setFallbackEmail(formData.email || '');
        }
        setCodeMessage(
          data.fallbackChannel === 'email'
            ? 'Après 3 tentatives incorrectes, vous pouvez recevoir le code par email.'
            : 'Après 3 tentatives incorrectes, vous pouvez recevoir le code par appel vocal.',
        );
      } else if (data.expired) {
        setCodeMessage('Ce code a expiré. Cliquez sur « Envoyer le code » pour en recevoir un nouveau.');
        if (data.fallbackAvailable && data.fallbackChannel) {
          setFallbackAvailable(true);
          setFallbackChannel(data.fallbackChannel);
          if (data.fallbackChannel === 'email') {
            setShowEmailFallback(true);
            setFallbackEmail(formData.email || '');
          }
        }
      } else if (typeof data.attemptsRemaining === 'number') {
        setCodeMessage(`Code incorrect. Il vous reste ${data.attemptsRemaining} tentative(s).`);
      }
    } finally {
      setVerifyingCode(false);
    }
  };

  // Inscription publique = Relais Public uniquement. Les Relais Privés rejoignent la
  // plateforme via le lien d'invitation d'un Parrain Grand Électeur (page RegisterRelais),
  // et les Relais Publics invités via le lien Admin/Gouverneur (page RegisterPublicRelais).
  // Aucune détection d'invitation par téléphone ici : ce formulaire crée toujours un Relais Public.

  // Capturer automatiquement la géolocalisation GPS en arrière-plan
  // Tente de capturer dès que le type de compte est sélectionné
  useEffect(() => {
    if (selectedAccountType && !formData.latitude && !formData.longitude && !capturingGeolocation) {
      captureGeolocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountType]);

  // Réessayer la capture GPS si l'adresse change (pour améliorer la précision)
  useEffect(() => {
    // Si on a une adresse complète mais pas de GPS, essayer de capturer
    const hasAddress = formData.commune_id || formData.city;
    if (hasAddress && !formData.latitude && !formData.longitude && !capturingGeolocation && selectedAccountType) {
      // Attendre un peu pour éviter trop de tentatives
      const timer = setTimeout(() => {
        captureGeolocation();
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.commune_id, formData.city, formData.region_id, formData.department_id]);

  // Fonction pour capturer la géolocalisation GPS en arrière-plan (silencieuse)
  const captureGeolocation = async () => {
    if (!navigator.geolocation) {
      setGpsStatus('not_available');
      return;
    }

    // Ne pas capturer si déjà en cours ou déjà capturé
    if (capturingGeolocation || (formData.latitude && formData.longitude)) {
      return;
    }

    setCapturingGeolocation(true);
    // Ne pas afficher de statut pour ne pas perturber l'utilisateur
    // setGpsStatus(null);

    const coords = await captureSilent();
    setCapturingGeolocation(false);
    if (coords) {
      setFormData(prev => ({
        ...prev,
        ...coords,
        geolocation_source: 'gps',
      }));
      setGpsStatus('captured');
    } else {
      // Erreur silencieuse - le backend géocodera l'adresse à la place
      console.log('GPS non disponible, géocodage automatique sera utilisé');
      setGpsStatus('failed');
    }
  };

  // La synchronisation du code téléphonique est maintenant gérée directement dans handleLocaliteChange

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrors({});
    
    // Validation côté client
    if (!formData.first_name || formData.first_name.trim().length < 2) {
      setError('Le prénom est requis et doit contenir au moins 2 caractères');
      return;
    }

    if (!formData.last_name || formData.last_name.trim().length < 2) {
      setError('Le nom est requis et doit contenir au moins 2 caractères');
      return;
    }

    if (!formData.profession) {
      setErrors((prev) => ({ ...prev, profession: 'Veuillez choisir une profession' }));
      setError('Veuillez choisir une profession');
      return;
    }

    // Éligibilité 18 ans (porte C) — avant le reste, comme dans le formulaire : le refus
    // le plus disqualifiant doit arriver en premier.
    if (selectedAccountType === 'beneficiaire') {
      if (!formData.date_naissance) {
        setErrors((prev) => ({ ...prev, date_naissance: 'La date de naissance est requise' }));
        setError('Indiquez votre date de naissance pour vérifier votre éligibilité au vote.');
        return;
      }
      // Fail-closed comme les portes A et B : on exige un verdict CONFIRMÉ, pas
      // simplement l'absence de refus — un échec réseau ne doit pas rouvrir l'envoi.
      if (!eligibility?.canVote) {
        setError(
          eligibility?.reason
          || 'Votre éligibilité au vote n\'a pas pu être confirmée. Vérifiez votre date de naissance et le scrutin choisi.'
        );
        return;
      }
    }

    const phoneFormatCheck = validateFullPhoneForSubmit(formData.telephone, formData.country_code);
    if (!phoneFormatCheck.valid) {
      setError(phoneFormatCheck.message);
      return;
    }

    // Valider l'adresse selon le pays
    const isSenegal = formData.country === 'Sénégal' || formData.country === 'Senegal' || formData.country_code === 'SN';
    
    // Validation spécifique Parrain Grand Électeur
    if (selectedAccountType === 'parrain_grand') {
      if (isSenegal && !formData.region_id) {
        setError('La région d\'origine est requise pour un Parrain Grand Électeur');
        return;
      }
    }
    
    // Validation spécifique Relais
    if (selectedAccountType === 'relais') {
      if (isSenegal) {
        if (!formData.region_id || !formData.department_id || !formData.commune_id) {
          setError('Pour un Relais, la région, le département et la commune sont obligatoires');
          return;
        }
        if (!formData.quartier || !String(formData.quartier).trim()) {
          setError('Le quartier ou village est obligatoire pour un Relais');
          return;
        }
      } else if (!formData.city || !String(formData.city).trim()) {
        setError('Pour un Relais à l\'étranger, la ville est obligatoire');
        return;
      }
      // Si Relais Privé, vérifier qu'il y a une invitation
      if (formData.relais_type === 'prive' && !formData.relais_invitation) {
        setError('Un Relais Privé doit être invité par un Parrain Grand Électeur. Aucune invitation trouvée pour ce numéro de téléphone.');
        return;
      }
    }
    
    // Validation adresse pour les autres types (Bénéficiaire, Parrain Sympathisant)
    if (selectedAccountType !== 'parrain_grand' && selectedAccountType !== 'relais') {
      if (isSenegal) {
        // Pour le Sénégal, valider région/département/commune
        if (!formData.region_id || !formData.department_id || !formData.commune_id) {
          setError('Veuillez sélectionner une région, un département et une commune');
          return;
        }
      } else {
        // Pour les autres pays, valider état/ville/code postal
        if (!formData.state || !formData.city || !formData.postal_code) {
          setError('Veuillez remplir l\'état/province, la ville et le code postal');
          return;
        }
      }
    }

    // Validation motivation pour relais public (auto-inscription, pas via invitation)
    if (selectedAccountType === 'relais' && !formData.relais_invitation) {
      if (!formData.motivation_type) {
        setError('Veuillez choisir un format de motivation (texte ou audio).');
        return;
      }
      if (formData.motivation_type === 'text' && !formData.motivation_text?.trim()) {
        setError('Veuillez décrire votre motivation en quelques phrases.');
        return;
      }
      if (formData.motivation_type === 'audio' && !motivationVoice?.blob) {
        setError('Veuillez enregistrer un message vocal de motivation.');
        return;
      }
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    // Vérifier les exigences du mot de passe
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(formData.password)) {
      setError('Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre');
      return;
    }

    if (!eulaAccepted) {
      setError('Veuillez lire et accepter les conditions d\'utilisation pour finaliser votre inscription.');
      return;
    }

    if (phoneCountryMismatch && selectedAccountType) {
      if (isStrictPhoneCountryAlignment(selectedAccountType)) {
        setError(getPhoneCountryMismatchMessage(formData.country_code));
        return;
      }
      if (requiresPhoneCountryMismatchAck(selectedAccountType) && !phoneCountryMismatchAck) {
        setError(
          'Votre numéro et votre pays de résidence ne correspondent pas. ' +
          'Cochez la case de confirmation ou alignez le pays et l\'indicatif.',
        );
        return;
      }
    }

    if (otpRequired && (!phoneVerified || !phoneVerificationToken)) {
      setError('Veuillez vérifier votre numéro de téléphone avec le code reçu (dernière étape avant de vous inscrire).');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, motivation_audio, relais_invitation, ...registerData } = formData;

      // Note vocale : encodée pour l'inscription (le backend crée le profil Relais)
      let motivationAudioPayload = {};
      if (
        selectedAccountType === 'relais'
        && formData.motivation_type === 'audio'
        && motivationVoice?.blob
      ) {
        motivationAudioPayload = {
          motivation_audio_base64: await blobToBase64(motivationVoice.blob),
          motivation_audio_mime: motivationVoice.mime || 'audio/webm',
          motivation_audio_duration: motivationVoice.durationS || null,
        };
      }
      
      // Normaliser les valeurs vides en null pour les UUIDs et champs optionnels
      const normalizeValue = (value) => {
        if (value === '' || value === null || value === undefined) {
          return null;
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          return trimmed === '' ? null : trimmed;
        }
        return value;
      };
      
      // Préparer les données d'inscription selon le type
      const registrationData = {
        ...registerData,
        // Normaliser les UUIDs et champs optionnels
        region_id: normalizeValue(registerData.region_id),
        department_id: normalizeValue(registerData.department_id),
        commune_id: normalizeValue(registerData.commune_id),
        quartier: normalizeValue(registerData.quartier),
        state: normalizeValue(registerData.state),
        city: normalizeValue(registerData.city),
        postal_code: normalizeValue(registerData.postal_code),
        address_line: normalizeValue(registerData.address_line),
        email: normalizeValue(registerData.email),
        // Éligibilité (porte C) : uniquement pour un compte Bénéficiaire (S1). Le serveur
        // rejoue le résolveur et enregistre le scrutin RÉSOLU, pas cette valeur.
        date_naissance: selectedAccountType === 'beneficiaire'
          ? normalizeValue(registerData.date_naissance)
          : null,
        election_id: selectedAccountType === 'beneficiaire'
          ? normalizeValue(registerData.election_id)
          : null,
        // Champs spécifiques Relais
        ...(selectedAccountType === 'relais' && {
          relais_type: formData.relais_type,
          localite_id: isSenegal ? normalizeValue(formData.commune_id) : null,
          motivation_type: formData.motivation_type || null,
          motivation_text:
            formData.motivation_type === 'text'
              ? normalizeValue(formData.motivation_text)
              : null,
          ...motivationAudioPayload,
        }),
        ...(otpRequired && phoneVerificationToken
          ? { phone_verification_token: phoneVerificationToken }
          : {}),
        ...(phoneCountryMismatch && requiresPhoneCountryMismatchAck(selectedAccountType)
          ? { phone_country_mismatch_acknowledged: phoneCountryMismatchAck }
          : {}),
        // Acceptation des conditions d'utilisation (exigée côté serveur désormais)
        eula_accepted: eulaAccepted,
      };

      const result = await register(registrationData);
      
      if (result.success) {
        // Profil Relais : créé côté backend à l'inscription. Fallback POST si besoin (anciennes versions).
        if (selectedAccountType === 'relais' && result.data?.user) {
          try {
            const relaisData = {
              type: formData.relais_type,
              localite_id: isSenegal ? formData.commune_id : null,
              quartier_village: formData.quartier?.trim() || formData.city?.trim() || null,
              motivation_type: formData.motivation_type || null,
              motivation_text:
                formData.motivation_type === 'text' ? (formData.motivation_text || null) : null,
              ...motivationAudioPayload,
            };

            await api.post('/relais', relaisData);
          } catch (relaisError) {
            const relaisErrMsg = relaisError.response?.data?.error || '';
            if (!relaisErrMsg.includes('existe déjà')) {
              console.error('Erreur lors de la création du profil Relais:', relaisError);
              setError(
                relaisErrMsg ||
                  'Votre compte a été créé mais le profil Relais n\'a pas pu être enregistré. Contactez le support avant validation.',
              );
              setLoading(false);
              return;
            }
          }
        }
        
        // Statut renvoyé par le backend : 'actif' (compte utilisable tout de suite)
        // ou 'en_attente' (validation par un Gouverneur requise).
        const statut = result.data?.user?.statut;
        const isActive = statut === 'actif';

        // Compte actif immédiatement (ex. Bénéficiaire, Relais Public sur invitation) :
        // auto-login déjà effectué → on va directement au tableau de bord.
        if (isActive) {
          const activeMsg = 'Inscription réussie ! Bienvenue sur Grand-Électeur. Votre compte est actif.';
          if (result.data?.token) {
            navigate('/dashboard', { state: { message: activeMsg } });
          } else {
            navigate('/login', { state: { message: activeMsg } });
          }
          return;
        }

        // Compte en attente de validation : on NE laisse PAS l'utilisateur sur un
        // tableau de bord qui renverrait « compte en attente ». On efface la session
        // auto-créée et on affiche un écran de confirmation clair.
        logout({ skipRedirect: true });

        // Canal d'avis de validation : appel téléphonique pour US/Canada, SMS ailleurs
        // (même logique de segmentation que l'OTP).
        const notifyChannel = describePrimaryOtpChannel(formData.country_code).primary === 'voice'
          ? 'un appel téléphonique'
          : 'un SMS';

        const accountLabel = selectedAccountType === 'relais'
          ? 'Relais Public'
          : selectedAccountType === 'parrain_grand'
            ? 'Parrain Grand Électeur'
            : 'Parrain Sympathisant';

        setRegistrationDone({
          accountLabel,
          notifyChannel,
          firstName: formData.first_name || '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      } else {
        // Afficher les erreurs détaillées si disponibles
        if (result.details && Array.isArray(result.details)) {
          const fieldErrors = {};
          result.details.forEach((err) => {
            const field = err.field || err.param || err.path;
            if (field) {
              fieldErrors[field] = err.message || err.msg;
            }
          });
          setErrors(fieldErrors);
          
          // Afficher aussi le message général
          if (result.error) {
            setError(result.error);
          }
        } else {
          setError(result.error || 'Erreur d\'inscription');
        }
      }
    } catch (err) {
      // Gérer les erreurs de réseau ou autres
      const errorMessage = err.response?.data?.error || err.message || 'Une erreur est survenue. Veuillez réessayer.';
      const errorDetails = err.response?.data?.details;
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const fieldErrors = {};
        errorDetails.forEach((errDetail) => {
          const field = errDetail.field || errDetail.param || errDetail.path;
          if (field) {
            fieldErrors[field] = errDetail.message || errDetail.msg;
          }
        });
        setErrors(fieldErrors);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1 className="register-title">Grand-Électeur</h1>
          <h2 className="register-subtitle">Inscription</h2>
        </div>
        
        {registrationDone ? (
          /* Écran de confirmation : compte créé, en attente de validation */
          <div className="registration-success" role="status" aria-live="polite">
            <div className="registration-success-icon" aria-hidden="true">🎉</div>
            <h3 className="registration-success-title">
              Bienvenue{registrationDone.firstName ? `, ${registrationDone.firstName}` : ''} sur Grand-Électeur !
            </h3>
            <p className="registration-success-text">
              Votre compte <strong>{registrationDone.accountLabel}</strong> a bien été créé.
              Félicitations et merci de rejoindre le mouvement.
            </p>
            <div className="registration-success-pending">
              <span className="registration-success-pending-icon" aria-hidden="true">⏳</span>
              <div>
                <strong>Compte en attente de validation</strong>
                <p>
                  Un Gouverneur va examiner votre inscription. Dès que votre compte est validé,
                  vous recevrez {registrationDone.notifyChannel} pour vous l'annoncer. Vous pourrez
                  alors vous connecter et accéder à votre espace.
                </p>
              </div>
            </div>
            <div className="registration-success-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setRegistrationDone(null);
                  navigate('/');
                }}
              >
                Aller à l'accueil
              </button>
            </div>
          </div>
        ) : !selectedAccountType ? (
          /* Sélection du type de compte */
          <div className="account-type-selection">
            <p className="account-type-intro">Choisissez votre type de compte pour commencer</p>
            <div className="account-type-grid">
              {USER_TYPES.map((type) => (
                <div
                  key={type.value}
                  className="account-type-card"
                  onClick={() => handleAccountTypeSelect(type.value)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Sélectionner ${type.label}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAccountTypeSelect(type.value);
                    }
                  }}
                >
                  <div className="account-type-icon" aria-hidden="true"><RoleIcon role={type.role} /></div>
                  <h3 className="account-type-title">{type.label}</h3>
                  <p className="account-type-description">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Formulaire d'inscription */
          <div className="registration-form">
            {/* En-tête avec type sélectionné */}
            <div className="form-header">
              <button
                type="button"
                className="back-to-selection-btn"
                onClick={() => {
                  setSelectedAccountType(null);
                  setFormData({ ...formData, type: '' });
                }}
                aria-label="Retour à la sélection du type de compte"
              >
                ← Retour
              </button>
              <div className="selected-account-type-badge" role="status" aria-label={`Type de compte sélectionné: ${USER_TYPES.find(t => t.value === selectedAccountType)?.label}`}>
                <span className="selected-account-icon" aria-hidden="true">
                  <RoleIcon role={USER_TYPES.find(t => t.value === selectedAccountType)?.role} />
                </span>
                <span className="selected-account-label">
                  {USER_TYPES.find(t => t.value === selectedAccountType)?.label}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} aria-label="Formulaire d'inscription">

          {/* Section Identite */}
          <section className="form-card" aria-labelledby="reg-section-identity">
            <div className="form-card-header">
              <span className="form-card-header-icon" aria-hidden="true">&#128100;</span>
              <h3 id="reg-section-identity" className="form-card-header-title">Identité</h3>
            </div>
            <div className="form-card-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label htmlFor="first_name">
                    Prénom <span className="required" aria-label="requis">*</span>
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Votre prénom"
                    required
                    disabled={loading}
                    minLength={2}
                    maxLength={100}
                    className={errors.first_name ? 'error' : ''}
                    aria-invalid={!!errors.first_name}
                    aria-describedby={errors.first_name ? 'first_name-error' : undefined}
                  />
                  {errors.first_name && (
                    <div id="first_name-error" className="field-error" role="alert">
                      {errors.first_name}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">
                    Nom <span className="required" aria-label="requis">*</span>
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    required
                    disabled={loading}
                    minLength={2}
                    maxLength={100}
                    className={errors.last_name ? 'error' : ''}
                    aria-invalid={!!errors.last_name}
                    aria-describedby={errors.last_name ? 'last_name-error' : undefined}
                  />
                  {errors.last_name && (
                    <div id="last_name-error" className="field-error" role="alert">
                      {errors.last_name}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="profession">
                  Profession <span className="required" aria-label="requis">*</span>
                </label>
                <select
                  id="profession"
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className={errors.profession ? 'error' : ''}
                  aria-invalid={!!errors.profession}
                  aria-describedby={
                    errors.profession ? 'profession-error profession-hint' : 'profession-hint'
                  }
                >
                  <option value="" disabled>
                    Choisir une profession…
                  </option>
                  {professionOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p id="profession-hint" className="form-hint">
                  Pour mieux comprendre qui utilise Grand-Électeur (rapports anonymisés).
                </p>
                {errors.profession && (
                  <div id="profession-error" className="field-error" role="alert">
                    {errors.profession}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Section Éligibilité — Bénéficiaire uniquement (porte C, v4.64.0).
              Placée juste après l'identité : inutile de faire choisir une commune puis un
              mot de passe à quelqu'un qui n'aura pas 18 ans au scrutin qu'il vise. */}
          {selectedAccountType === 'beneficiaire' && (
            <section className="form-card" aria-labelledby="reg-section-eligibility">
              <div className="form-card-header">
                <span className="form-card-header-icon" aria-hidden="true">&#128499;&#65039;</span>
                <h3 id="reg-section-eligibility" className="form-card-header-title">
                  Éligibilité au vote
                </h3>
              </div>
              <div className="form-card-body">
                <ElectionEligibilityField
                  dateNaissance={formData.date_naissance}
                  onDateNaissanceChange={(value) => {
                    setFormData((prev) => ({ ...prev, date_naissance: value }));
                    setErrors((prev) => ({ ...prev, date_naissance: null }));
                  }}
                  electionId={formData.election_id}
                  onElectionIdChange={(value) =>
                    setFormData((prev) => ({ ...prev, election_id: value }))
                  }
                  onEligibilityChange={setEligibility}
                  audience="citoyen"
                  disabled={loading}
                  error={errors.date_naissance || ''}
                />
              </div>
            </section>
          )}

          {/* Section Localisation */}
          <section className="form-card" aria-labelledby="reg-section-address">
            <div className="form-card-header">
              <span className="form-card-header-icon" aria-hidden="true">&#128205;</span>
              <h3 id="reg-section-address" className="form-card-header-title">Adresse</h3>
            </div>
            <div className="form-card-body">
              <LocaliteSelector
                country={formData.country}
                countryCode={formData.country_code}
                regionId={formData.region_id}
                departmentId={formData.department_id}
                communeId={formData.commune_id}
                quartier={formData.quartier}
                state={formData.state}
                city={formData.city}
                postal_code={formData.postal_code}
                address_line={formData.address_line}
                onChange={handleLocaliteChange}
                errors={errors}
                required={true}
                requireQuartier={selectedAccountType === 'relais'}
                showCountrySelector={true}
              />
              {isRelaisOrBeneficiaire && (
                <small className="form-hint">
                  Sénégal par défaut ; la diaspora peut choisir son pays (consulats).
                </small>
              )}
              {gpsStatus === 'captured' && formData.geolocation_source === 'gps' && (
                <div className="gps-status-container" role="status" aria-live="polite">
                  <span aria-label="Position GPS capturee automatiquement">Position GPS capturee</span>
                </div>
              )}
            </div>
          </section>

          {/* Section Coordonnees */}
          <section className="form-card" aria-labelledby="reg-section-contact">
            <div className="form-card-header">
              <span className="form-card-header-icon" aria-hidden="true">&#128222;</span>
              <h3 id="reg-section-contact" className="form-card-header-title">Coordonnées</h3>
            </div>
            <div className="form-card-body">
              <div className="form-group">
                <label htmlFor="telephone">
                  Téléphone <span className="required" aria-label="requis">*</span>
                </label>
                <CountryPhoneInput
                  id="telephone"
                  value={formData.telephone}
                  onChange={handlePhoneChange}
                  countryCode={formData.country_code}
                  lockCountryPrefix
                  errors={errors}
                  disabled={
                    loading ||
                    (selectedAccountType === 'relais' && !!formData.relais_invitation)
                  }
                  aria-describedby={
                    selectedAccountType === 'relais' && formData.relais_invitation
                      ? 'telephone-invitation-hint'
                      : undefined
                  }
                />
                {selectedAccountType === 'relais' && formData.relais_invitation && (
                  <small className="form-hint" id="telephone-invitation-hint">
                    Ce numéro est lié à votre invitation Relais Privé et ne peut pas être modifié.
                  </small>
                )}
                {isRelaisOrBeneficiaire && !formData.relais_invitation && (
                  <small className="form-hint">
                    Indicatif lié au pays d&apos;adresse. Sénégal : 9 chiffres locaux après +221.
                  </small>
                )}
                {!isRelaisOrBeneficiaire && selectedAccountType && (
                  <small className="form-hint">
                    Indicatif lié au pays d&apos;adresse — saisissez uniquement le numéro local (sans indicatif).
                  </small>
                )}
                {phoneCountryMismatch && selectedAccountType && (
                  <div
                    className={`phone-country-mismatch ${isStrictPhoneCountryAlignment(selectedAccountType) ? 'phone-country-mismatch--error' : 'phone-country-mismatch--warn'}`}
                    role="alert"
                  >
                    <p>{getPhoneCountryMismatchMessage(formData.country_code)}</p>
                    {requiresPhoneCountryMismatchAck(selectedAccountType) && (
                      <label className="phone-country-mismatch__ack">
                        <input
                          type="checkbox"
                          checked={phoneCountryMismatchAck}
                          onChange={(e) => setPhoneCountryMismatchAck(e.target.checked)}
                        />
                        <span>
                          J'utilise volontairement un numero etranger (ex. ligne familiale ou professionnelle)
                          et je confirme que mon pays de résidence déclaré est correct.
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email (optionnel)</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre.email@example.com"
                  disabled={loading}
                  className={errors.email ? 'error' : ''}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <div id="email-error" className="field-error" role="alert">
                    {errors.email}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Invitation Relais Prive */}
          {selectedAccountType === 'relais' && formData.relais_invitation && (
            <div className="form-info-card">
              <h4>Invitation recue</h4>
              <p><strong>Parrain :</strong> {formData.relais_invitation.parrain_name}</p>
              <small className="form-hint">
                Vous serez assigné comme Relais Privé pour ce Parrain.
                Vous travaillerez exclusivement pour lui dans ses campagnes.
              </small>
            </div>
          )}

          {/* Relais Public — info */}
          {selectedAccountType === 'relais' && !formData.relais_invitation && (
            <section className="form-card" aria-labelledby="reg-section-relais-public">
              <div className="form-card-header">
                <span className="form-card-header-icon" aria-hidden="true">&#128226;</span>
                <h3 id="reg-section-relais-public" className="form-card-header-title">Relais Public</h3>
              </div>
              <div className="form-card-body">
                <p className="form-hint">
                  Vous vous inscrivez comme <strong>Relais Public</strong> : vous accompagnez les
                  bénéficiaires de votre zone et menez vos propres campagnes d'enrôlement sur le terrain.
                  Votre compte sera active apres validation par un Gouverneur.
                </p>
              </div>
            </section>
          )}

          {/* Section Securite */}
          <section className="form-card" aria-labelledby="reg-section-security">
            <div className="form-card-header">
              <span className="form-card-header-icon" aria-hidden="true">&#128274;</span>
              <h3 id="reg-section-security" className="form-card-header-title">Sécurité</h3>
            </div>
            <div className="form-card-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <PasswordInput
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    label={
                      <>
                        Mot de passe <span className="required" aria-label="requis">*</span>
                      </>
                    }
                    placeholder="Mot de passe"
                    required
                    disabled={loading}
                    minLength={8}
                    autoComplete="new-password"
                    error={errors.password}
                    hint="Min. 8 caractères, majuscule, minuscule, chiffre"
                    hintId="password-hint"
                  />
                </div>

                <div className="form-group">
                  <PasswordInput
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    label={
                      <>
                        Confirmer <span className="required" aria-label="requis">*</span>
                      </>
                    }
                    placeholder="Confirmer le mot de passe"
                    required
                    disabled={loading}
                    minLength={8}
                    autoComplete="new-password"
                    hint="Repetez votre mot de passe"
                    hintId="confirm-password-hint"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Section Motivation (Relais) */}
          {formData.type === 'relais' && (
            <section className="form-card" aria-labelledby="reg-section-motivation">
              <div className="form-card-header">
                <span className="form-card-header-icon" aria-hidden="true">&#128172;</span>
                <h3 id="reg-section-motivation" className="form-card-header-title">Motivation</h3>
              </div>
              <div className="form-card-body">
                <p className="form-hint">
                  Expliquez pourquoi vous souhaitez devenir Relais.
                </p>
                
                <div className="form-group">
                  <label htmlFor="motivation_type">Format</label>
                  <select
                    id="motivation_type"
                    name="motivation_type"
                    value={formData.motivation_type}
                    onChange={handleChange}
                    disabled={loading}
                    required
                    aria-required="true"
                    className="select-with-placeholder"
                  >
                    <option value="" disabled hidden>Type de motivation</option>
                    <option value="text">Texte</option>
                    <option value="audio">Audio</option>
                  </select>
                </div>

                {formData.motivation_type === 'text' && (
                  <div className="form-group">
                    <label htmlFor="motivation_text">
                      Pourquoi voulez-vous etre relais ? <span className="required">*</span>
                    </label>
                    <textarea
                      id="motivation_text"
                      name="motivation_text"
                      value={formData.motivation_text}
                      onChange={handleChange}
                      placeholder="Decrivez votre motivation..."
                      required
                      disabled={loading}
                      maxLength={500}
                      rows={4}
                      className={errors.motivation_text ? 'error' : ''}
                    />
                    {errors.motivation_text && <div className="field-error">{errors.motivation_text}</div>}
                    <small className="form-hint">
                      {formData.motivation_text.length}/500 caractères
                    </small>
                  </div>
                )}

                {formData.motivation_type === 'audio' && (
                  <div className="form-group">
                    <label>
                      Message vocal <span className="required">*</span>
                    </label>
                    <VoiceNoteRecorder onChange={setMotivationVoice} disabled={loading} />
                    <small className="form-hint">
                      Maintenez le micro pour enregistrer (max 2 min), puis reecoutez avant de soumettre.
                    </small>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Section Verification OTP */}
          {otpRequired && (
            <section className="form-card" aria-labelledby="reg-section-otp">
              <div className="form-card-header">
                <span className="form-card-header-icon" aria-hidden="true">&#128241;</span>
                <h3 id="reg-section-otp" className="form-card-header-title">Vérification</h3>
              </div>
              <div className="form-card-body">
                <div className={`form-group phone-verification-block ${phoneVerified ? 'is-verified' : ''}`}>
                  {!phoneVerified && (
                    <p className="form-hint">
                      Confirmez le numéro saisi plus haut ({formData.telephone || '—'}).
                      {' '}
                      Un code sera envoye par SMS ou par appel telephonique.
                    </p>
                  )}
                  {!phoneVerified && (
                    <div className="phone-verification-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleSendPhoneCode()}
                        disabled={loading || sendingCode || resendCooldown > 0 || !phoneReadyForOtp}
                      >
                        {sendingCode
                          ? 'Envoi…'
                          : resendCooldown > 0
                            ? `Renvoyer (${resendCooldown}s)`
                            : 'Envoyer le code'}
                      </button>
                    </div>
                  )}
                  {phoneVerified ? (
                    <div className="phone-verified-banner" role="status" aria-live="polite">
                      <span className="phone-verified-icon" aria-hidden="true">✓</span>
                      <div className="phone-verified-text">
                        <strong>Numéro vérifié</strong>
                        <span>{formData.telephone} est confirmé. Vous pouvez finaliser l'inscription.</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="phone-verification-code-row">
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="Code a 6 chiffres"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          disabled={loading || verifyingCode}
                          aria-label="Code de verification"
                        />
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={handleVerifyPhoneCode}
                          disabled={loading || verifyingCode || verificationCode.length < 4}
                        >
                          {verifyingCode ? 'Vérification…' : 'Vérifier'}
                        </button>
                      </div>
                      {codeMessage && (
                        <small
                          className={`form-hint ${!mockOtpCode && codeMessage.includes('OTP Configuration') ? 'mock-blocked-hint' : ''}`}
                          role="status"
                        >
                          {codeMessage}
                        </small>
                      )}
                    </>
                  )}

                  {mockOtpCode && !phoneVerified && (
                    <div className="mock-otp-banner" role="status" aria-live="polite">
                      <p className="mock-otp-banner-title">Mode test — code de verification</p>
                      <p className="mock-otp-banner-hint">
                        Reserve aux testeurs lorsque la simulation est activee. Ce bloc n&apos;apparait jamais en production live.
                      </p>
                      <div className="mock-otp-banner-code-row">
                        <span className="mock-otp-code" aria-label="Code OTP de test">{mockOtpCode}</span>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(mockOtpCode);
                              setCodeMessage('Code copie dans le presse-papiers.');
                            } catch {
                              setCodeMessage('Copie impossible — selectionnez le code manuellement.');
                            }
                          }}
                        >
                          Copier
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => setVerificationCode(mockOtpCode)}
                        >
                          Remplir le champ
                        </button>
                      </div>
                    </div>
                  )}

                  {!phoneVerified && fallbackAvailable && fallbackChannel === 'voice' && (
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => handleSendPhoneCode('voice')}
                      disabled={sendingCode || resendCooldown > 0}
                    >
                      {otpPreview.fallbackLabel}
                    </button>
                  )}

                  {!phoneVerified && fallbackAvailable && fallbackChannel === 'email' && showEmailFallback && (
                    <div className="email-fallback-block">
                      <p className="form-hint">
                        Saisissez l'adresse email ou recevoir votre code de verification.
                      </p>
                      <div className="phone-verification-code-row">
                        <input
                          type="email"
                          autoComplete="email"
                          placeholder="adresse@email.com"
                          value={fallbackEmail}
                          onChange={(e) => setFallbackEmail(e.target.value)}
                          disabled={loading || sendingEmailCode}
                          aria-label="Email pour le code de verification"
                        />
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={handleSendEmailCode}
                          disabled={loading || sendingEmailCode || !fallbackEmail}
                        >
                          {sendingEmailCode ? 'Envoi…' : 'Envoyer par email'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* EULA + Submit */}
          <div className="form-group eula-consent">
            <label className="eula-checkbox-label">
              <input
                type="checkbox"
                checked={eulaAccepted}
                onChange={(e) => {
                  setEulaAccepted(e.target.checked);
                  if (error) setError('');
                }}
                disabled={loading}
                aria-required="true"
              />
              <span>
                J'ai lu et j'accepte les{' '}
                <button
                  type="button"
                  className="btn-link eula-link"
                  onClick={() => setShowEula(true)}
                >
                  conditions d'utilisation
                </button>
                .
              </span>
            </label>
          </div>

          <div className="form-submit-row">
            <button 
              type="submit" 
              disabled={
                loading
                || !eulaAccepted
                // Bénéficiaire : envoi fermé tant que l'éligibilité n'est pas confirmée.
                || (selectedAccountType === 'beneficiaire' && !eligibility?.canVote)
              }
              className="btn-primary"
              aria-label={loading ? 'Inscription en cours...' : 'Soumettre le formulaire d\'inscription'}
            >
              {loading ? 'Inscription...' : 'S\'inscrire'}
            </button>
          </div>

          <EulaModal open={showEula} onClose={() => setShowEula(false)} />
        </form>
          </div>
        )}
        
        <div className="form-footer">
          <p>
            Déjà un compte ? <Link to="/login">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
